import { http } from '@/services/api';

export type StudyScheduleStatus = 'completed' | 'redo' | 'incomplete' | 'redone';

export interface StudyScheduleDTO {
  id: number;
  contact_id: number;
  user_id?: number;
  team_id?: number | null;
  // Eager-loaded responsible team (optional)
  team?: { id: number; name: string } | null;
  // Contact/agenda region (optional, used for gating and UI)
  region_id?: number;
  study_material_id: number; // maps to Nº Estudo
  study_date: string; // Y-m-d
  study_time: string; // H:i or H:i:s
  status: StudyScheduleStatus;
  reschedule_count: number;
  notes?: string | null;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ListFilters {
  contact_id?: number;
  start_date?: string; // Y-m-d
  end_date?: string;   // Y-m-d
  status?: StudyScheduleStatus;
  sort_by?: 'study_date' | 'created_at' | 'status' | 'updated_at';
  sort_dir?: 'asc' | 'desc';
  per_page?: number;
  user_id?: number;
  team_id?: number;
  region_id?: number;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number | string;
  total: number;
}

export const studySchedulesService = {
  async list(filters: ListFilters = {}): Promise<Paginated<StudyScheduleDTO>> {
    const params = new URLSearchParams();
    if (filters.contact_id) params.set('contact_id', String(filters.contact_id));
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (filters.status) params.set('status', filters.status);
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
    if (filters.per_page) params.set('per_page', String(filters.per_page));
    if (filters.user_id) params.set('user_id', String(filters.user_id));
    if (filters.team_id) params.set('team_id', String(filters.team_id));
    if (filters.region_id) params.set('region_id', String(filters.region_id));
    return http.get<Paginated<StudyScheduleDTO>>(`/study-schedules?${params.toString()}`);
  },

  async upcoming(params: { user_id?: number; start_date?: string; end_date?: string; limit?: number } = {}): Promise<{ data: StudyScheduleDTO[] }>{
    const qs = new URLSearchParams();
    if (params.user_id) qs.set('user_id', String(params.user_id));
    if (params.start_date) qs.set('start_date', params.start_date);
    if (params.end_date) qs.set('end_date', params.end_date);
    if (params.limit) qs.set('limit', String(params.limit));
    return http.get<{ data: StudyScheduleDTO[] }>(`/study-schedules/upcoming?${qs.toString()}`);
  },

  async create(payload: {
    contact_id: number;
    team_id: number;
    study_material_id: number;
    study_date: string; // Y-m-d
    study_time: string; // H:i
    notes?: string | null;
    status?: StudyScheduleStatus; // defaults to backend default (incomplete)
  }): Promise<StudyScheduleDTO> {
    return http.post<StudyScheduleDTO>(`/study-schedules`, payload);
  },

  async update(id: number, payload: Partial<Pick<StudyScheduleDTO, 'study_material_id' | 'study_date' | 'study_time' | 'notes' | 'status'>>): Promise<StudyScheduleDTO> {
    const res = await http.put<{ data?: StudyScheduleDTO } | StudyScheduleDTO>(`/study-schedules/${id}`, payload);
    // Normalize to StudyScheduleDTO
    if (res && typeof (res as any) === 'object' && 'data' in (res as any)) {
      return (res as any).data as StudyScheduleDTO;
    }
    return res as StudyScheduleDTO;
  },

  async complete(id: number): Promise<StudyScheduleDTO> {
    return http.patch<StudyScheduleDTO>(`/study-schedules/${id}/complete`);
  },

  async cancel(id: number): Promise<StudyScheduleDTO> {
    return http.patch<StudyScheduleDTO>(`/study-schedules/${id}/cancel`);
  },
};
